import { Link, useLocation } from "wouter";
import { getCategoryById, getToolById } from "@/lib/tools-data";
import type { KnowledgeBaseSearchResults } from "@/lib/knowledgeBaseSearch";
import { Route, Search, HelpCircle, User, BookOpen } from "lucide-react";
import { parsePipelineRequest } from "@/lib/pipelineParser";
import { AssistantCard } from "@/components/AssistantCard";
import { recordSearchClick } from "@/lib/searchTelemetry";

interface SearchResultsDropdownProps {
  results: KnowledgeBaseSearchResults;
  query: string;
  onSelectTool: (toolId: string) => void;
  /** Called on any click-through that isn't a tool (persona, blog post,
   * starting a pipeline) -- typically just closes the dropdown, since
   * navigation itself is handled directly. */
  onDismiss?: () => void;
}

/**
 * Shared presentation for both the header search and the homepage hero
 * search -- grouped Multi-step pipeline/Tools/For Your Work/Guides &
 * Articles/Good to know, in that priority order. A detected multi-step
 * request goes first, since it's a stronger, more specific signal than
 * any individual tool match -- someone typing "merge my pdfs and
 * compress them" is better served by the guided pipeline than by
 * scrolling past a Tools list to find it. Otherwise tools stay first,
 * since that's almost always what someone searching this site actually
 * wants. Fed by the MiniSearch-based knowledge base search, plus
 * pipelineParser.ts run directly against the raw query for the
 * multi-step case (curated workflow results replaced by this dynamic
 * detection -- see pipelineParser.ts's own header comment).
 */
export function SearchResultsDropdown({
  results,
  query,
  onSelectTool,
  onDismiss,
}: SearchResultsDropdownProps) {
  const [, navigate] = useLocation();
  const { tools, faq, personas, blog, assistant, noMatch } = results;
  // Suppressed whenever the AI already answered -- see the matching
  // comment in SearchSidePanel.tsx for why: this plain keyword guess has
  // no awareness of what the AI decided and was rendering above its
  // workflow answer with none of its nuance.
  const pipelineMatch = !assistant && !noMatch && query.trim().length >= 6 ? parsePipelineRequest(query) : null;
  const hasResults = tools.length > 0 || faq.length > 0 || personas.length > 0 || blog.length > 0 || !!pipelineMatch || !!assistant;

  const startPipeline = () => {
    if (!pipelineMatch) return;
    const idsParam = pipelineMatch.map((m) => m.tool.id).join(",");
    recordSearchClick(`/pipeline?tools=${encodeURIComponent(idsParam)}`, "pipeline", 0);
    navigate(`/pipeline?tools=${encodeURIComponent(idsParam)}`);
    onDismiss?.();
  };

  if (!hasResults) {
    return (
      <div className="absolute top-full mt-2 w-full bg-popover border border-border rounded-lg shadow-lg z-50 px-4 py-6 text-center">
        <Search className="h-4 w-4 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No matches for "{query}"</p>
      </div>
    );
  }

  return (
    <div className="absolute top-full mt-2 w-full bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden max-h-[70vh] overflow-y-auto">
      {assistant && (
        <AssistantCard
          assistant={assistant}
          trigger="mousedown"
          onNavigate={(href, kind, position) => {
            recordSearchClick(href, kind, position);
            navigate(href);
            onDismiss?.();
          }}
        />
      )}
      {pipelineMatch && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wide mb-2">
            <Route className="h-3.5 w-3.5" />
            This looks like a multi-step request
          </div>
          <p className="text-sm mb-1.5">To do this, you'd need {pipelineMatch.length} tools in sequence:</p>
          <ol className="text-sm mb-2 pl-5 list-decimal space-y-0.5">
            {pipelineMatch.map((m) => (
              <li key={m.tool.id}>{m.tool.name}</li>
            ))}
          </ol>
          <button
            onMouseDown={startPipeline}
            className="text-sm font-medium text-primary hover:underline"
          >
            Yes, guide me through it &rarr;
          </button>
        </div>
      )}

      {noMatch && (
        <p className="px-4 pt-3 pb-1 text-sm font-medium text-foreground" role="status">
          No tool matches &ldquo;{query.trim()}&rdquo;
          {/shorten|short\s*-?link|short\s*url|url\s*shortener/i.test(query) && (
            <span className="block mt-1 text-xs font-normal text-muted-foreground">The URL shortener was retired. These are the closest tools.</span>
          )}
        </p>
      )}
      {tools.length > 0 && (
        <div>
          <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${pipelineMatch ? "border-t border-border/60" : ""}`}>
            {noMatch ? "Closest tools" : "Tools"}
          </p>
          {tools.map((doc, index) => {
            const toolId = doc.id.replace(/^tool-/, "");
            const tool = getToolById(toolId);
            const category = tool ? getCategoryById(tool.category) : undefined;
            const Icon = category?.icon;
            // Not every entry in `tools` is backed by a real, existing
            // tool -- the optional AI-assist path in knowledgeBaseSearch.ts
            // can prepend a synthetic result (single AI-picked tool, or a
            // multi-tool workflow pointing at /pipeline?tools=...) whose id
            // doesn't match a real tool-${slug}. onSelectTool always
            // navigates to /tool/${toolId}, which only makes sense for a
            // real tool, so anything else falls back to doc.href directly.
            const handleClick = tool
              ? () => {
                  recordSearchClick(`/tool/${toolId}`, "tool", index);
                  onSelectTool(toolId);
                }
              : () => {
                  recordSearchClick(doc.href, doc.href.startsWith("/pipeline") ? "pipeline" : "tool", index);
                  navigate(doc.href);
                  onDismiss?.();
                };
            return (
              <button
                key={doc.id}
                className="w-full px-4 py-2.5 text-left hover:bg-accent transition-colors flex items-start gap-3"
                onMouseDown={handleClick}
              >
                {Icon && <Icon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: category?.color }} />}
                <div>
                  <p className="text-sm font-medium text-foreground">{doc.title}</p>
                  <p className="text-xs text-muted-foreground">{doc.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {personas.length > 0 && (
        <div>
          <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${tools.length > 0 || pipelineMatch ? "border-t border-border/60" : ""}`}>
            For Your Work
          </p>
          {personas.map((doc, i) => (
            <Link key={doc.id} href={doc.href} onMouseDown={() => { recordSearchClick(doc.href, "persona", i); onDismiss?.(); }}>
              <a className="block w-full px-4 py-2.5 text-left hover:bg-accent transition-colors">
                <div className="flex items-start gap-3">
                  <User className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{doc.title}</p>
                    <p className="text-xs text-muted-foreground">{doc.description}</p>
                  </div>
                </div>
              </a>
            </Link>
          ))}
        </div>
      )}

      {blog.length > 0 && (
        <div>
          <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${tools.length > 0 || pipelineMatch || personas.length > 0 ? "border-t border-border/60" : ""}`}>
            Guides &amp; Articles
          </p>
          {blog.map((doc, i) => (
            <Link key={doc.id} href={doc.href} onMouseDown={() => { recordSearchClick(doc.href, "blog", i); onDismiss?.(); }}>
              <a className="block w-full px-4 py-2.5 text-left hover:bg-accent transition-colors">
                <div className="flex items-start gap-3">
                  <BookOpen className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{doc.title}</p>
                    <p className="text-xs text-muted-foreground">{doc.description}</p>
                  </div>
                </div>
              </a>
            </Link>
          ))}
        </div>
      )}

      {faq.length > 0 && (
        <div>
          <p className={`px-4 pt-3 pb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide ${tools.length > 0 || pipelineMatch || personas.length > 0 || blog.length > 0 ? "border-t border-border/60" : ""}`}>
            Good to know
          </p>
          {faq.map((doc) => (
            <div key={doc.id} className="px-4 py-2.5 flex items-start gap-3">
              <HelpCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">{doc.title}</p>
                <p className="text-xs text-muted-foreground">{doc.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
